import {Plain} from '../../../../../shared/model/plain.type.model';
import {buildEmptyCondition, Condition} from './condition.model';
import {ConditionPageOption} from './condition-page-option.model';
import {CONDITION_TYPE} from './condition.type.model';
import {buildEmptyGroup, Group} from './group.model';
import {OPERATOR_LOGICAL} from './operator.logical.model';

export const ALWAYS_DISPLAYED = 'ALWAYS_DISPLAYED';
export const CONDITIONAL_DISPLAY = 'CONDITIONAL_DISPLAY';

/**
 * Une condition est-elle remplie de bout en bout ?
 *
 * Elle se lit dans l'ordre — ce qu'elle observe, comment elle le compare, à quoi — et ces trois
 * moments doivent tous être renseignés. Il en manque un, et elle ne décide de rien : le
 * questionnaire ne saurait pas quand montrer ce qu'elle règle.
 *
 * La valeur est lue plutôt que comptée : le calendrier vidé laisse derrière lui une valeur vide, et
 * non pas aucune valeur. Le critère d'un multicritères, lui, n'est pas exigé — seules les questions
 * qui en portent le proposent.
 */
const isConditionComplete = (condition: Condition): boolean => {
    const observed =
        condition.type === CONDITION_TYPE.ANSWER
            ? condition.pageId !== undefined && condition.questionId !== undefined
            : condition.metadataId !== undefined;

    return observed && !!condition.operator && !!condition.values[0];
};

/**
 * Une condition observe-t-elle ce choix précis ?
 *
 * La question est vérifiée en même temps que le choix : deux questions différentes peuvent porter
 * des choix aux identifiants voisins, et une condition sur métadonnée n'observe aucun choix du tout.
 */
const observesChoice = (condition: Condition, questionId: number, choiceId: number): boolean =>
    condition.type === CONDITION_TYPE.ANSWER &&
    condition.questionId === questionId &&
    condition.values.includes(`${choiceId}`);

/**
 * Une condition observe-t-elle ce critère précis d'une question multicritères ?
 *
 * Le critère se reconnaît à son identifiant et non à la valeur attendue, contrairement à un choix :
 * une condition sur multicritères désigne le critère qu'elle observe, et compare ensuite la note
 * qu'il a reçue. La question est vérifiée en même temps que lui, deux questions pouvant porter des
 * critères aux identifiants voisins.
 */
const observesSubQuestion = (condition: Condition, questionId: number, subQuestionId: number): boolean =>
    condition.type === CONDITION_TYPE.ANSWER &&
    condition.questionId === questionId &&
    condition.subQuestionId === subQuestionId;

/**
 * Une condition observe-t-elle une réponse qui n'est plus donnée avant la cible qu'elle règle ?
 *
 * Elle ne serait alors jamais vraie : ce qu'elle règle ne s'afficherait plus jamais. C'est ce qui
 * arrive à une cible qu'un déplacement fait passer devant ce qu'elle observait, ou devant laquelle
 * passe ce qu'elle observait.
 *
 * `sources` est ce que les sélecteurs offrent de viser depuis la cible : la question de la comparer
 * à eux est exactement celle que l'utilisateur y verrait, page choisie puis question choisie.
 *
 * Une condition laissée à moitié composée compte quand même, dès lors qu'elle désigne une page : la
 * page peut ne plus précéder, et c'est déjà une condition que plus rien ne pourra rendre vraie. Une
 * condition qui ne désigne encore rien du tout n'en est pas — elle reste à faire où que la cible se
 * trouve, et une condition sur métadonnée vaut partout, le répondant la portant avant d'avoir rien
 * répondu.
 */
const observesUnreachableAnswer = (condition: Condition, sources: ConditionPageOption[]): boolean => {
    if (condition.type !== CONDITION_TYPE.ANSWER) return false;

    const source = sources.find((offered) => offered.pageId === condition.pageId);
    if (!source) return condition.pageId !== undefined && condition.pageId !== null;

    return (
        condition.questionId !== undefined &&
        condition.questionId !== null &&
        !source.questions.some((question) => question.questionId === condition.questionId)
    );
};

/**
 * Ce qui décide qu'une page ou un élément se montre au répondant.
 *
 * Les conditions se lisent comme une phrase : à l'intérieur d'un groupe, les conditions se relient
 * par `Group.operator` ; les groupes entre eux, par l'`operator` porté ici. Aucun groupe signifie
 * « toujours affiché » — le mode n'est pas une donnée à part.
 *
 * Toutes les retouches renvoient une nouvelle instance : le brouillon ne se modifie jamais sur
 * place, c'est son remplacement qui déclenche l'enregistrement.
 */
export class DisplayOptions {
    public operator: OPERATOR_LOGICAL;
    public groups: Group[];

    /**
     * L'API omet les options d'affichage d'une page ou d'un élément toujours affiché : on retombe
     * alors sur un jeu de conditions vide, qui est précisément la définition de « toujours affiché ».
     */
    constructor(data?: Plain<DisplayOptions> | null) {
        this.operator = data?.operator ?? OPERATOR_LOGICAL.AND;
        this.groups = (data?.groups ?? []).map((group) => new Group(group));
    }

    /**
     * Ce qui part au serveur : rien du tout, quand rien ne conditionne l'affichage.
     *
     * L'API distingue « toujours affiché » par l'**absence** d'options, et non par un jeu de
     * conditions vide. Un `{operator, groups: []}` lui dit au contraire « affichage conditionnel »,
     * dont aucune condition n'est remplie : elle masque alors la page ou l'élément, et le
     * questionnaire s'ouvre sur sa page de fin.
     *
     * C'est la lecture inverse de celle du constructeur, qui accueille l'absence d'options. Elle est
     * posée ici, sur le point de passage obligé de la sérialisation, plutôt que dans l'appel qui
     * enregistre le brouillon : aucun autre chemin ne peut alors l'oublier.
     */
    public readonly toJSON = (): Plain<DisplayOptions> | null =>
        this.groups.length === 0 ? null : {operator: this.operator, groups: this.groups};

    /**
     * Ces conditions sont-elles toutes remplies ?
     *
     * Une condition à moitié composée ne décide de rien : la page ou l'élément qu'elle règle ne
     * s'afficherait jamais, et le questionnaire s'arrêterait là. C'est ce qui interdit de diffuser
     * une enquête qui en porterait une.
     *
     * Aucun groupe, rien à remplir : « toujours affiché » est complet par nature. Un groupe sans
     * condition, en revanche, ne l'est pas — il n'y aurait rien à satisfaire pour s'afficher.
     */
    public readonly isComplete = (): boolean =>
        this.groups.every(
            (group) =>
                group.conditions.length > 0 && group.conditions.every((condition) => isConditionComplete(condition))
        );

    /** Relie les groupes entre eux : tous doivent être vrais, ou l'un d'eux suffit. */
    public readonly withOperator = (operator: OPERATOR_LOGICAL): DisplayOptions =>
        new DisplayOptions({operator, groups: this.groups});

    /** Relie entre elles les conditions d'un même groupe. */
    public readonly withGroupOperator = (groupIndex: number, operator: OPERATOR_LOGICAL): DisplayOptions =>
        this.mapGroups((group, index) => (index === groupIndex ? {...group, operator} : group));

    /** Un groupe s'ouvre avec une condition à remplir : vide, il n'offrirait rien à saisir. */
    public readonly withGroupAdded = (conditionType?: CONDITION_TYPE): DisplayOptions =>
        new DisplayOptions({operator: this.operator, groups: [...this.groups, buildEmptyGroup(conditionType)]});

    /**
     * Retire un groupe. Le dernier retiré laisse l'élément sans condition, c'est-à-dire toujours
     * affiché — c'est bien ce que veut dire supprimer sa seule condition d'affichage.
     */
    public readonly withGroupRemoved = (groupIndex: number): DisplayOptions =>
        new DisplayOptions({
            operator: this.operator,
            groups: this.groups.filter((_, index) => index !== groupIndex),
        });

    public readonly withConditionAdded = (groupIndex: number, conditionType?: CONDITION_TYPE): DisplayOptions =>
        this.mapGroups((group, index) =>
            index === groupIndex
                ? {...group, conditions: [...group.conditions, buildEmptyCondition(conditionType)]}
                : group
        );

    /**
     * Retire une condition, et son groupe avec elle si c'était la dernière : un groupe sans
     * condition ne se distinguerait pas d'un groupe absent.
     */
    public readonly withConditionRemoved = (groupIndex: number, conditionIndex: number): DisplayOptions => {
        const group = this.groups[groupIndex];
        if (group && group.conditions.length <= 1) return this.withGroupRemoved(groupIndex);

        return this.mapGroups((current, index) =>
            index === groupIndex
                ? {...current, conditions: current.conditions.filter((_, rank) => rank !== conditionIndex)}
                : current
        );
    };

    /**
     * Retouche une condition. Les champs absents du correctif ne bougent pas : changer d'opérateur
     * ne doit pas oublier la question visée.
     */
    public readonly withCondition = (
        groupIndex: number,
        conditionIndex: number,
        patch: Partial<Condition>
    ): DisplayOptions =>
        this.mapGroups((group, index) =>
            index === groupIndex
                ? {
                      ...group,
                      conditions: group.conditions.map((condition, rank) =>
                          rank === conditionIndex ? {...condition, ...patch} : condition
                      ),
                  }
                : group
        );

    /** Vrai si l'une de ces conditions observe le choix désigné. */
    public readonly observesChoice = (questionId: number, choiceId: number): boolean =>
        this.groups.some((group) =>
            group.conditions.some((condition) => observesChoice(condition, questionId, choiceId))
        );

    /** Vrai si l'une de ces conditions observe le critère désigné. */
    public readonly observesSubQuestion = (questionId: number, subQuestionId: number): boolean =>
        this.groups.some((group) =>
            group.conditions.some((condition) => observesSubQuestion(condition, questionId, subQuestionId))
        );

    /**
     * Combien de ces conditions observent une réponse qui n'est plus donnée avant la cible.
     *
     * `sources` est ce que les sélecteurs offrent de viser depuis la cible : c'est à l'appelant, qui
     * seul connaît le parcours, de le dire.
     *
     * Un compte plutôt qu'un oui ou non : ce qui se demande d'un déplacement, c'est s'il en rompt de
     * nouvelles, et cela se lit en comparant l'avant et l'après.
     */
    public readonly unreachableAnswerCount = (sources: ConditionPageOption[]): number =>
        this.groups.reduce(
            (count, group) =>
                count + group.conditions.filter((condition) => observesUnreachableAnswer(condition, sources)).length,
            0
        );

    /**
     * Ces conditions, débarrassées de celles qui observaient un choix supprimé.
     *
     * La condition entière s'en va, et non la seule valeur : c'est ce que l'avertissement promet à
     * l'utilisateur avant qu'il confirme.
     */
    public readonly withoutChoice = (questionId: number, choiceId: number): DisplayOptions =>
        this.without((condition) => observesChoice(condition, questionId, choiceId));

    /**
     * Ces conditions, débarrassées de celles qui observaient un critère supprimé.
     *
     * La condition entière s'en va, comme pour un choix : ce qu'elle observait n'est plus noté par
     * personne, et c'est ce que l'avertissement promet avant que l'utilisateur confirme.
     */
    public readonly withoutSubQuestion = (questionId: number, subQuestionId: number): DisplayOptions =>
        this.without((condition) => observesSubQuestion(condition, questionId, subQuestionId));

    /**
     * Ces conditions, débarrassées de celles qui observent une réponse qui n'est plus donnée avant la
     * cible.
     *
     * Elles seules s'en vont : celles qui restent vraies restent, et une condition sur métadonnée
     * vaut où que la cible se trouve. C'est ce qu'il faut d'un déplacement qui bouscule le parcours —
     * retirer plus serait défaire ce que l'utilisateur n'a pas demandé de défaire.
     */
    public readonly withoutUnreachableAnswers = (sources: ConditionPageOption[]): DisplayOptions =>
        this.without((condition) => observesUnreachableAnswer(condition, sources));

    /**
     * Ces conditions, débarrassées de celles que le filtre écarte.
     *
     * Un groupe vidé part avec ses conditions — un groupe sans condition ne se distinguerait pas
     * d'un groupe absent —, et une cible qui perd son dernier groupe redevient toujours affichée, ce
     * qui est bien ce qu'il reste quand plus rien ne la conditionne.
     */
    private readonly without = (rejected: (condition: Condition) => boolean): DisplayOptions =>
        new DisplayOptions({
            operator: this.operator,
            groups: this.groups
                .map((group) => ({...group, conditions: group.conditions.filter((condition) => !rejected(condition))}))
                .filter((group) => group.conditions.length > 0),
        });

    private readonly mapGroups = (update: (group: Group, index: number) => Plain<Group>): DisplayOptions =>
        new DisplayOptions({operator: this.operator, groups: this.groups.map(update)});
}
